import { useCallback, useState } from "react"
import styled from "styled-components"

import {
  Badge,
  Button,
  colors,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"

import { ParadiseTable } from "src/components/Paradise/ParadiseTable"
import { useFetchHomeGuests } from "src/data/guests/queries/guestQueries"
import { TGuest } from "src/data/guests/types/guestTypes"
import { useFetchParadiseHomes } from "src/data/paradise/paradiseHomes/queries/paradiseHomeQueries"
import { IParadiseHome } from "src/data/paradise/paradiseHomes/types/paradiseHomeQuerytypes"
import { usePostParadiseLLMPromptTestMessage } from "src/data/paradise/paradiseLLMPrompts/queries/paradiseLLMPromptsQueries"
import { TParadiseLLMPrompt } from "src/data/paradise/paradiseLLMPrompts/types/paradiseLLMPromptsQueryTypes"
import { DropdownSelect } from "src/ui/DropdownSelect/DropdownSelect"
import { Dropzone } from "src/ui/Dropzone/Dropzone"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import { MCard } from "src/ui/MCard/MCard"
import { MCircularProgress } from "src/ui/MCircularProgress/MCircularProgress"

type TBulkTestResult = {
  id: string
  question: string
  response: Record<string, unknown> | string
  status: "pending" | "success" | "error"
  error?: string
}

function parseResponse(response: string): Record<string, unknown> | string {
  if (!response.startsWith("{") && !response.startsWith("[")) {
    return response
  }

  try {
    return JSON.parse(response) as Record<string, unknown>
  } catch (e) {
    return response
  }
}

export function ParadiseLLMPromptBulkTesting({
  prompt,
}: {
  prompt: TParadiseLLMPrompt
}) {
  const [file, setFile] = useState<File | null>(null)
  const [questions, setQuestions] = useState<string[]>([])
  const [results, setResults] = useState<TBulkTestResult[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [selectedHome, setSelectedHome] = useState<IParadiseHome | null>(null)
  const [selectedGuest, setSelectedGuest] = useState<TGuest | null>(null)
  const [searchedHomeId, setSearchedHomeId] = useState("")
  const [fileError, setFileError] = useState<string | undefined>(undefined)

  const fetchHomes = useFetchParadiseHomes({
    filter: {
      home_id: searchedHomeId,
    },
    options: {
      keepPreviousData: true,
    },
  })

  const fetchHomesGuests = useFetchHomeGuests({
    homeId: selectedHome?.id ?? "",
    options: {
      enabled: !!selectedHome,
    },
  })

  const postParadiseLLMPromptTestMessage = usePostParadiseLLMPromptTestMessage()

  async function parseCSVFile(file: File): Promise<string[]> {
    const text = await file.text()
    // Split by lines and filter out empty lines
    return text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
  }

  async function handleFileChange(newFile: File | null) {
    setFile(newFile)
    setQuestions([])
    setResults([])
    setFileError(undefined)

    if (newFile) {
      try {
        const parsedQuestions = await parseCSVFile(newFile)
        setQuestions(parsedQuestions)
      } catch (error) {
        setFileError(
          "Failed to parse CSV file. Please ensure the file is valid and contains one question per line."
        )
      }
    }
  }

  const processQuestions = useCallback(async () => {
    if (!questions.length) {
      return
    }

    setIsProcessing(true)
    setResults(
      questions.map((question, index) => ({
        id: index.toString(),
        question,
        response: "",
        status: "pending" as const,
      }))
    )

    // Process questions sequentially to avoid overwhelming the API
    for (const [i, question] of questions.entries()) {
      try {
        const response = await postParadiseLLMPromptTestMessage.mutateAsync({
          path: {
            prompt_id: prompt.id,
          },
          body: {
            input: question,
            home_id: selectedHome?.id,
            guest_id: selectedGuest?.guest_id,
          },
        })

        setResults((prev) =>
          prev.map((result, index) =>
            index === i
              ? {
                  ...result,
                  response: parseResponse(response.response || ""),
                  status: "success" as const,
                }
              : result
          )
        )
      } catch (error) {
        setResults((prev) =>
          prev.map((result, index) =>
            index === i
              ? {
                  ...result,
                  status: "error" as const,
                  error:
                    error instanceof Error ? error.message : "Unknown error",
                }
              : result
          )
        )
      }

      // Add a small delay between requests
      if (i < questions.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 100))
      }
    }

    setIsProcessing(false)
  }, [
    questions,
    prompt.id,
    selectedHome?.id,
    selectedGuest?.guest_id,
    postParadiseLLMPromptTestMessage,
  ])

  function clearResults() {
    setResults([])
    setQuestions([])
    setFile(null)
  }

  // Get all unique keys from successful responses
  const keys = new Set<string>()
  results.forEach((result) => {
    if (result.status === "success" && typeof result.response === "object") {
      Object.keys(result.response).forEach((key) => {
        // Hide responseId from the table since it's irrelevant for the user
        if (key !== "responseId") {
          keys.add(key)
        }
      })
    }
  })
  const allKeys = Array.from(keys).sort()

  // Create table columns dynamically
  const baseColumns: TableColumn<TBulkTestResult>[] = [
    {
      value: "question",
      label: "Question",
      columnWidth: "auto",
      renderCell: (data) => (
        <WrappedText variant="body">{data.question}</WrappedText>
      ),
    },
  ]

  // Add dynamic columns for each response key
  const responseColumns: TableColumn<TBulkTestResult>[] = allKeys.map(
    (key) => ({
      value: key,
      label: key,
      columnWidth: "auto",
      renderCell: (data) => (
        <WrappedText variant="body">
          {data.status === "success" && typeof data.response === "object"
            ? String(data.response[key] || "-")
            : data.status === "success" && typeof data.response === "string"
              ? key === "response"
                ? data.response
                : "-"
              : "-"}
        </WrappedText>
      ),
    })
  )

  const statusColumn: TableColumn<TBulkTestResult> = {
    value: "status",
    label: "Status",
    columnWidth: "auto",
    renderCell: (data) => (
      <div>
        {data.status === "pending" ? (
          <MCircularProgress size={20} color={colors.primary} />
        ) : (
          <Badge color={data.status === "success" ? "good" : "error"}>
            {data.status === "success" && "Success"}
            {data.status === "error" && "Error"}
          </Badge>
        )}
        {data.error && (
          <WrappedText variant="bodyS" color="emergency">
            {data.error}
          </WrappedText>
        )}
      </div>
    ),
  }

  const tableColumns: TableColumn<TBulkTestResult>[] = [
    ...baseColumns,
    ...responseColumns,
    statusColumn,
  ]

  const { headerElements, rows, templateColumns } =
    useTableColumns<TBulkTestResult>({
      columns: tableColumns,
      data: results,
    })

  return (
    <StyledCard border boxShadow={false} padding={0}>
      <Header>
        <Text variant="heading3">Bulk Testing</Text>
      </Header>

      <Content>
        <Section>
          <Text variant="subtitle" marginBottom={spacing.S}>
            Upload CSV file with questions (one per line)
          </Text>
          <Dropzone
            file={file}
            onChange={(file) => void handleFileChange(file)}
            loading={false}
            inputProps={{ accept: "text/csv" }}
            description="Drop a CSV file here or click to browse"
            error={fileError}
          />
        </Section>

        {questions.length > 0 && (
          <Section>
            <Text variant="subtitle" marginBottom={spacing.S}>
              Configuration (Optional)
            </Text>
            <ConfigWrapper>
              <DropdownSelect
                label={selectedHome?.name ?? "Select home"}
                searchText={searchedHomeId}
                onSearch={setSearchedHomeId}
                options={
                  fetchHomes.data?.homes.map((home) => ({
                    label: home.name,
                    value: home.id,
                    description: home.id,
                    home,
                  })) ?? []
                }
                selectedValue={selectedHome?.id ?? null}
                onChange={({ option }) => setSelectedHome(option.home)}
                showCheckmark={false}
              />
              <DropdownSelect
                label={selectedGuest?.name ?? "Select guest"}
                options={
                  fetchHomesGuests.data?.guests.map((guest) => ({
                    label: guest.name,
                    value: guest.guest_id,
                    description: guest.guest_id,
                    guest,
                  })) ?? []
                }
                selectedValue={selectedGuest?.guest_id ?? null}
                onChange={({ option }) => setSelectedGuest(option.guest)}
                showCheckmark={false}
                disabled={!selectedHome}
              />
            </ConfigWrapper>
          </Section>
        )}

        {questions.length > 0 && (
          <Section>
            <Text variant="subtitle" marginBottom={spacing.S}>
              {questions.length} questions ready to test
            </Text>
            <ButtonWrapper>
              <Button
                color="good"
                size="small"
                loading={isProcessing}
                disabled={isProcessing}
                onClick={() => void processQuestions()}
              >
                {isProcessing ? "Processing..." : "Start Testing"}
              </Button>
              <Button
                color="neutral"
                size="small"
                onClick={clearResults}
                disabled={isProcessing}
              >
                Clear
              </Button>
            </ButtonWrapper>
          </Section>
        )}

        {results.length > 0 && (
          <Section>
            <Text variant="subtitle" marginBottom={spacing.S}>
              Results
            </Text>
            <ParadiseTable
              header={headerElements}
              rows={rows}
              templateColumns={templateColumns}
              removeMargin
            />
          </Section>
        )}
      </Content>
    </StyledCard>
  )
}

const StyledCard = styled(MCard)`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.M};
  border-bottom: 1px solid ${colors.divider};
`

const Content = styled.div`
  flex: 1;
  padding: ${spacing.M};
  overflow-y: auto;
`

const Section = styled.div`
  margin-bottom: ${spacing.L};

  &:last-child {
    margin-bottom: 0;
  }
`

const ConfigWrapper = styled.div`
  display: flex;
  gap: ${spacing.S};
  flex-wrap: wrap;
`

const ButtonWrapper = styled.div`
  display: flex;
  gap: ${spacing.S};
`

const WrappedText = styled(Text)`
  word-wrap: break-word;
  white-space: pre-wrap;
  margin: 0;
  padding: ${spacing.XS};
`
